import React from "react";
import Reveal from "./Reveal";
import { ArrowUpRight } from "lucide-react";
import { JOURNAL_POSTS, FEATURED_SLUG } from "../../data/journalPosts";
import { trackEvent } from "../../lib/track";

// Three teasers: the featured post plus the two most recent non-featured.
// The journalPosts module is ordered by publication date.
const buildTeasers = () => {
  const featured = JOURNAL_POSTS.find((p) => p.slug === FEATURED_SLUG) || JOURNAL_POSTS[0];
  const others = JOURNAL_POSTS.filter((p) => p.slug !== featured.slug).slice(0, 2);
  return [featured, ...others];
};

const TEASERS = buildTeasers();

/**
 * Journal Teaser.
 *
 * Three featured posts as quiet entry points into the Journal
 * archive. Each card links to its own post; the section-level CTA
 * goes to the full archive. The headline is intentionally written
 * to meet the visitor in the question they may be carrying.
 */
const JournalTeaser = () => (
  <section
    id="journal-teaser"
    data-testid="journal-teaser-section"
    className="relative py-24 md:py-32 border-t border-gold/10"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
          The Journal
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-themed font-light text-center max-w-3xl mx-auto">
          Still trying to name what you are{" "}
          <span className="italic gold-gradient-text">feeling?</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TEASERS.map((p, i) => (
          <Reveal key={p.slug} delay={120 + i * 80}>
            <a
              href={`/journal/${p.slug}`}
              data-testid={`journal-teaser-${p.slug}`}
              onClick={() => trackEvent("journal_teaser_clicked", { slug: p.slug })}
              className="journal-teaser-card group block h-full bg-white/5 border-t border-gold/20 p-8 rounded-lg relative focus:outline-none focus:ring-1 focus:ring-gold/60"
            >
              <ArrowUpRight
                size={16}
                strokeWidth={1.4}
                className="absolute top-5 right-5 text-gold/60 group-hover:text-gold transition-colors"
                aria-hidden
              />
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-3">
                {p.tag || "Reflection"}
              </div>
              <h3 className="font-serif text-xl md:text-[1.4rem] text-themed group-hover:text-gold transition-colors leading-snug">
                {p.title}
              </h3>
              {p.excerpt ? (
                <p className="mt-4 text-white/85 text-[14.5px] leading-[1.7] line-clamp-3">
                  {p.excerpt}
                </p>
              ) : null}
              <div className="mt-6 text-[11px] tracking-[0.22em] uppercase text-gold/85">
                Read the reflection
              </div>
            </a>
          </Reveal>
        ))}
      </div>

      <Reveal delay={420}>
        <div className="mt-12 text-center">
          <a
            href="/journal"
            data-testid="journal-teaser-archive-cta"
            onClick={() => trackEvent("journal_teaser_archive_cta_clicked")}
            className="btn-ghost-gold inline-flex"
          >
            Read the Journal
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default JournalTeaser;
